import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { analyticsService } from '../services/analyticsService';
import type {
  AnalyticsState,
  AnalyticsFilters,
  MetricType,
  TimePeriod,
  TimeGranularity,
  ExportOptions,
  ReportConfig,
  GeneratedReport,
  PerformanceMetrics
} from '../types/analytics';

interface AnalyticsActions {
  // Dashboard management
  loadDashboard: (filters: AnalyticsFilters) => Promise<void>;
  refreshDashboard: () => Promise<void>;

  // Metrics management
  loadMetric: (
    metric: MetricType,
    period: TimePeriod,
    granularity: TimeGranularity,
    filters?: Partial<AnalyticsFilters>
  ) => Promise<void>;
  loadMultipleMetrics: (
    metrics: MetricType[],
    period: TimePeriod,
    granularity: TimeGranularity,
    filters?: Partial<AnalyticsFilters>
  ) => Promise<void>;

  // Filters management
  updateFilters: (filters: Partial<AnalyticsFilters>) => void;
  resetFilters: () => void;

  // Export and reporting
  exportData: (options: ExportOptions) => Promise<{ url: string; filename: string }>;
  generateReport: (config: ReportConfig) => Promise<GeneratedReport>;

  // Real-time updates
  enableRealTime: (interval?: number) => void;
  disableRealTime: () => void;

  // Performance tracking
  trackPerformance: (metrics: Partial<PerformanceMetrics>) => void;

  // Utility actions
  clearError: () => void;
  clearCache: () => void;
  setLoading: (section: keyof AnalyticsState['loading'], loading: boolean) => void;
}

type AnalyticsStore = AnalyticsState & AnalyticsActions;

const defaultFilters: AnalyticsFilters = {
  period: '7d',
  granularity: 'day',
  channels: ['email'],
  metrics: ['delivery_rate', 'open_rate', 'click_rate']
};

const initialState: AnalyticsState = {
  dashboard: null,
  metrics: {},
  filters: defaultFilters,
  loading: {
    dashboard: false,
    metrics: false,
    export: false,
  },
  error: null,
  lastUpdated: {
    dashboard: null,
    metrics: null,
  },
  realTime: {
    enabled: false,
    connected: false,
    updateInterval: 30000, // 30 seconds
  },
};

let realTimeInterval: NodeJS.Timeout | null = null;

export const useAnalyticsStore = create<AnalyticsStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // ============ DASHBOARD MANAGEMENT ============

      loadDashboard: async (filters: AnalyticsFilters) => {
        set((state) => ({
          ...state,
          loading: { ...state.loading, dashboard: true },
          error: null,
          filters
        }));

        const startTime = performance.now();

        try {
          const dashboard = await analyticsService.getDashboard(filters);
          
          const endTime = performance.now();
          const fetchTime = endTime - startTime;

          set((state) => ({
            ...state,
            dashboard,
            loading: { ...state.loading, dashboard: false },
            lastUpdated: { ...state.lastUpdated, dashboard: new Date().toISOString() },
          }));

          // Track performance
          get().trackPerformance({
            dataFetchTime: fetchTime,
            totalDataPoints: dashboard.communication.timeSeries.sends.dataPoints.length
          });

        } catch (error) {
          set((state) => ({
            ...state,
            loading: { ...state.loading, dashboard: false },
            error: error instanceof Error ? error.message : 'Failed to load dashboard',
          }));
          throw error;
        }
      },

      refreshDashboard: async () => {
        const { filters } = get();
        await get().loadDashboard(filters);
      },

      // ============ METRICS MANAGEMENT ============

      loadMetric: async (
        metric: MetricType,
        period: TimePeriod,
        granularity: TimeGranularity,
        filters?: Partial<AnalyticsFilters>
      ) => {
        set((state) => ({
          ...state,
          loading: { ...state.loading, metrics: true },
          error: null
        }));

        const startTime = performance.now();

        try {
          const timeSeriesData = await analyticsService.getMetricTimeSeries(
            metric,
            period,
            granularity,
            filters
          );

          const endTime = performance.now();
          const fetchTime = endTime - startTime;

          const metricKey = `${metric}_${period}_${granularity}`;
          
          set((state) => ({
            ...state,
            metrics: {
              ...state.metrics,
              [metricKey]: timeSeriesData
            },
            loading: { ...state.loading, metrics: false },
            lastUpdated: { ...state.lastUpdated, metrics: new Date().toISOString() },
          }));

          // Track performance
          get().trackPerformance({
            dataFetchTime: fetchTime,
            totalDataPoints: timeSeriesData.dataPoints.length
          });

        } catch (error) {
          set((state) => ({
            ...state,
            loading: { ...state.loading, metrics: false },
            error: error instanceof Error ? error.message : 'Failed to load metric',
          }));
          throw error;
        }
      },

      loadMultipleMetrics: async (
        metrics: MetricType[],
        period: TimePeriod,
        granularity: TimeGranularity,
        filters?: Partial<AnalyticsFilters>
      ) => {
        set((state) => ({
          ...state,
          loading: { ...state.loading, metrics: true },
          error: null
        }));

        const startTime = performance.now();

        try {
          const metricsData = await analyticsService.getMultipleMetrics(
            metrics,
            period,
            granularity,
            filters
          );

          const endTime = performance.now();
          const fetchTime = endTime - startTime;

          // Create metric keys and update state
          const updatedMetrics = { ...get().metrics };
          let totalDataPoints = 0;

          Object.entries(metricsData).forEach(([metric, data]) => {
            const metricKey = `${metric}_${period}_${granularity}`;
            updatedMetrics[metricKey] = data;
            totalDataPoints += data.dataPoints.length;
          });

          set((state) => ({
            ...state,
            metrics: updatedMetrics,
            loading: { ...state.loading, metrics: false },
            lastUpdated: { ...state.lastUpdated, metrics: new Date().toISOString() },
          }));

          // Track performance
          get().trackPerformance({
            dataFetchTime: fetchTime,
            totalDataPoints
          });

        } catch (error) {
          set((state) => ({
            ...state,
            loading: { ...state.loading, metrics: false },
            error: error instanceof Error ? error.message : 'Failed to load metrics',
          }));
          throw error;
        }
      },

      // ============ FILTERS MANAGEMENT ============

      updateFilters: (newFilters: Partial<AnalyticsFilters>) => {
        set((state) => ({
          ...state,
          filters: { ...state.filters, ...newFilters }
        }));
      },

      resetFilters: () => {
        set((state) => ({
          ...state,
          filters: defaultFilters
        }));
      },

      // ============ EXPORT AND REPORTING ============

      exportData: async (options: ExportOptions) => {
        set((state) => ({
          ...state,
          loading: { ...state.loading, export: true },
          error: null
        }));

        try {
          const result = await analyticsService.exportData(options);
          
          set((state) => ({
            ...state,
            loading: { ...state.loading, export: false }
          }));

          return result;
        } catch (error) {
          set((state) => ({
            ...state,
            loading: { ...state.loading, export: false },
            error: error instanceof Error ? error.message : 'Failed to export data',
          }));
          throw error;
        }
      },

      generateReport: async (config: ReportConfig) => {
        set((state) => ({
          ...state,
          loading: { ...state.loading, export: true },
          error: null
        }));

        try {
          const report = await analyticsService.generateReport(config);
          
          set((state) => ({
            ...state,
            loading: { ...state.loading, export: false }
          }));

          return report;
        } catch (error) {
          set((state) => ({
            ...state,
            loading: { ...state.loading, export: false },
            error: error instanceof Error ? error.message : 'Failed to generate report',
          }));
          throw error;
        }
      },

      // ============ REAL-TIME UPDATES ============

      enableRealTime: (interval = 30000) => {
        const store = get();
        
        // Clear existing interval
        if (realTimeInterval) {
          clearInterval(realTimeInterval);
        }

        // Set up new interval
        realTimeInterval = setInterval(async () => {
          try {
            await store.refreshDashboard();
          } catch (error) {
            console.error('Real-time update failed:', error);
          }
        }, interval);

        set((state) => ({
          ...state,
          realTime: {
            ...state.realTime,
            enabled: true,
            connected: true,
            updateInterval: interval
          }
        }));
      },

      disableRealTime: () => {
        if (realTimeInterval) {
          clearInterval(realTimeInterval);
          realTimeInterval = null;
        }

        set((state) => ({
          ...state,
          realTime: {
            ...state.realTime,
            enabled: false,
            connected: false
          }
        }));
      },

      // ============ PERFORMANCE TRACKING ============

      trackPerformance: (metrics: Partial<PerformanceMetrics>) => {
        const performanceData: PerformanceMetrics = {
          chartRenderTime: 0,
          dataFetchTime: 0,
          totalDataPoints: 0,
          memoryUsage: 0,
          updateLatency: 0,
          ...metrics
        };

        analyticsService.trackPerformance(performanceData);
      },

      // ============ UTILITY ACTIONS ============

      clearError: () => set((state) => ({ ...state, error: null })),

      clearCache: () => {
        analyticsService.clearCache();
        set((state) => ({
          ...state,
          metrics: {},
          dashboard: null,
          lastUpdated: {
            dashboard: null,
            metrics: null,
          }
        }));
      },

      setLoading: (section, loading) => 
        set((state) => ({
          ...state,
          loading: { ...state.loading, [section]: loading },
        })),

    }),
    {
      name: 'analytics-store',
      // Don't persist analytics data - it should be fresh
      partialize: (state: AnalyticsStore) => ({
        filters: state.filters,
        realTime: {
          enabled: state.realTime.enabled,
          updateInterval: state.realTime.updateInterval,
          connected: false // Don't persist connection state
        }
      }),
    }
  )
);

// Cleanup on module unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (realTimeInterval) {
      clearInterval(realTimeInterval);
    }
  });
}